/**
 * Colours of the onboarding design that the app theme has no token for, shared
 * by the wizard and the `/signup` pages. Everything the theme has (primary and
 * secondary blue, dividers, the `blue.default` tint for hover and selected
 * backgrounds) comes from the theme instead.
 */
export const ONBOARDING_COLORS = {
  /** Background of the brand panel and of the step sidebar (v2 design, `navy-2`). */
  brandPanel: "#14295A",
  /** Accent of the brand headline's second line (v2 design). */
  brandAccent: "#FA6450",
  /** Headings and other strong text. */
  heading: "#1A2340",
  /** Body text and descriptions. */
  body: "#4B5468",
  /** Secondary text inside tiles. */
  muted: "#6B7385",
  /** Border of an unselected tile or card. */
  border: "#E1E5EE",
  /** Lines of the signup page's "Or" divider. */
  divider: "#E1E5EE",
  /** Border of the signup language switch. */
  selectorBorder: "#CBD2E0",
  /** Border of the signup language switch on hover. */
  selectorBorderHover: "#aab3c3",
  /** Background of a highlighted card on the signup pages. */
  selectedBackground: "#eff4fa",
  /** Border of an input-like control (design `--line-2`). */
  fieldBorder: "#CBD2E0",
  /** The design's navy (`--navy`): selected borders and badges. */
  navy: "#1E3A78",
  /** The design system's button navy: icons and text buttons. */
  dsNavy: "#001E64",
  /** The design system's light blue: hover and selected backgrounds. */
  dsTint: "#F0F5FC",
  /** Error labels and hints (v220 design; the field line itself is #D21C10). */
  errorText: "#AF0900",
  /** Confirmation green. */
  success: "#1B7A5A",
  /** Dims the rail and page behind the done card (v220 design). */
  backdrop: "rgba(14, 30, 67, 0.56)",
} as const;

/** Horizontal page gutter: 96px at the designed 1440px width, tighter below. */
export const ONBOARDING_GUTTER = "clamp(16px, 6.67vw, 96px)";

/** Widest content column of a step. */
export const ONBOARDING_CONTENT_WIDTH = 884;

/**
 * Label of the design system's button (v220 boards `.btn`): 15px/26px, 500,
 * wide-tracked, uppercase. Spread it into a button's `sx` with
 * `ONBOARDING_BUTTON_HEIGHT`.
 */
export const ONBOARDING_BUTTON_LABEL = {
  border: "1px solid rgba(1, 30, 94, 0.5)",
  "& .MuiButton-endIcon": { mr: 0 },
  "& .MuiButton-startIcon": { ml: 0 },
  fontSize: 15,
  lineHeight: "26px",
  letterSpacing: "0.3px",
  fontWeight: 500,
  textTransform: "uppercase",
} as const;

/** Height of the design system's button. */
export const ONBOARDING_BUTTON_HEIGHT = 42;

/** The design system's small text button (`btn text sm`): 32px, 13px/22px, navy, light hover. */
export const ONBOARDING_SMALL_TEXT_BUTTON = {
  minWidth: 0,
  height: 32,
  px: "5px",
  border: "1px solid transparent",
  fontSize: 13,
  lineHeight: "22px",
  letterSpacing: "0.46px",
  fontWeight: 500,
  textTransform: "uppercase",
  color: ONBOARDING_COLORS.dsNavy,
  "&:hover": { bgcolor: ONBOARDING_COLORS.dsTint },
} as const;
